import cn from "classnames";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Loader } from "ui";

import {
  useGetEffectivePrompt,
  useGetExpertSettings,
  useUpdateExpertSettings
} from "../../apis";

import styles from "./PromptTab.module.scss";

const MAX_LENGTH = 20000;

const SaveIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M17.5013 1.6665C17.9615 1.6665 18.3346 2.0396 18.3346 2.49984V17.4998C18.3346 17.9601 17.9615 18.3332 17.5013 18.3332H2.5013C2.04106 18.3332 1.66797 17.9601 1.66797 17.4998V6.24984C1.66797 6.02882 1.75583 5.81692 1.91211 5.66064L5.66211 1.91064L5.72314 1.85531C5.87144 1.73378 6.05789 1.6665 6.2513 1.6665H17.5013ZM3.33464 6.59489V16.6665H4.16797V9.99984C4.16797 9.5396 4.54107 9.1665 5.0013 9.1665H15.0013C15.4615 9.1665 15.8346 9.5396 15.8346 9.99984V16.6665H16.668V3.33317H14.5846V7.49984C14.5846 7.96007 14.2115 8.33317 13.7513 8.33317H7.5013C7.04107 8.33317 6.66797 7.96007 6.66797 7.49984V3.33317H6.59635L3.33464 6.59489ZM5.83464 16.6665H14.168V10.8332H5.83464V16.6665ZM8.33464 6.6665H12.918V3.33317H8.33464V6.6665Z"
      fill="white"
    />
  </svg>
);

interface Props {
  assistantId: string;
  canEdit: boolean;
}

const PromptTab = ({ assistantId, canEdit }: Props) => {
  const { t } = useTranslation();
  const { effectivePrompt, isLoading } = useGetEffectivePrompt(assistantId);
  const { expertSettings } = useGetExpertSettings(assistantId, canEdit);
  const updateMutation = useUpdateExpertSettings(assistantId);

  const [value, setValue] = useState("");

  useEffect(() => {
    if (expertSettings) setValue(expertSettings.systemPrompt ?? "");
  }, [expertSettings]);

  if (isLoading) {
    return (
      <div className={styles.loaderWrap}>
        <Loader />
      </div>
    );
  }

  const handleSave = () =>
    updateMutation.mutate(
      { systemPrompt: value },
      { onSuccess: () => toast.success(t("Expert settings saved")) }
    );

  return (
    <div className={styles.wrapper}>
      {/* Read-only view of the live prompt — visible to every admin. */}
      <div className={styles.label}>{t("System Prompt")}</div>
      <div className={styles.hint}>
        {t(
          "The live prompt the assistant runs with: base instructions plus the enabled rules. Read-only. Per-conversation context is added automatically at runtime."
        )}
      </div>
      <textarea
        className={cn(styles.textarea, styles.readonly)}
        value={effectivePrompt?.systemPrompt ?? ""}
        readOnly
      />

      {/* Advanced override editor — super admins only, not yet wired to runtime. */}
      {canEdit && (
        <>
          <div className={styles.banner}>
            {t(
              "Advanced override — preview only. The saved value is not yet used by the AI runtime. Coming in a follow-up."
            )}
          </div>
          <div className={styles.label}>{t("System Prompt Override")}</div>
          <textarea
            className={styles.textarea}
            value={value}
            maxLength={MAX_LENGTH}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t("Raw system prompt...")}
          />
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={updateMutation.isLoading}
          >
            <SaveIcon />
            {t("Save")}
          </button>
        </>
      )}
    </div>
  );
};

export default PromptTab;
